import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Trash2, Edit } from 'lucide-react';
import { DEFAULT_IMAGE_URL } from '../lib/images';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';
const DEFAULT_HOMEPAGE_SECTIONS = [
  {
    section: 'video_showcase',
    title: 'Premium Auto Showcase',
    description: 'Discover this week\'s featured vehicle - a masterpiece of engineering and design.',
    cta_text: 'Find Out More',
    cta_link: '/reviews',
    video_url: '',
    image_url: ''
  },
  {
    section: 'newsletter',
    title: 'Get Our Latest News & Update',
    description: 'Subscribe to our newsletter and stay updated with the latest automotive news.',
    cta_text: 'Submit Now',
    cta_link: '',
    video_url: '',
    image_url: ''
  }
];

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join(' | ');
  }
  return fallback;
};

const safeJsonParse = (value, fieldName) => {
  if (!value || !String(value).trim()) return {};
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error(`${fieldName} must be a JSON object`);
    }
    return parsed;
  } catch (err) {
    throw new Error(`${fieldName} is invalid JSON`);
  }
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host.includes('youtube.com')) {
      if (parsed.pathname === '/watch') {
        const videoId = parsed.searchParams.get('v');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return url;
      }
      if (parsed.pathname.startsWith('/v/')) {
        const videoId = parsed.pathname.split('/')[2];
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.slice(1);
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    return null;
  } catch (err) {
    return null;
  }
};

export const AdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [cars, setCars] = useState([]);
  const [homepageContent, setHomepageContent] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterSearch, setNewsletterSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingItem, setEditingItem] = useState(null); // { type: 'article' | 'car', id: string }


  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'articles') {
        const { data } = await axios.get(`${API_URL}/api/articles?limit=100`);
        setArticles(data);
      } else if (activeTab === 'cars') {
        const { data } = await axios.get(`${API_URL}/api/cars`);
        setCars(data);
      } else if (activeTab === 'homepage') {
        const { data } = await axios.get(`${API_URL}/api/homepage-content`);
        setHomepageContent(Array.isArray(data) && data.length ? data : DEFAULT_HOMEPAGE_SECTIONS);
      } else if (activeTab === 'contact') {
        const { data } = await axios.get(`${API_URL}/api/contact-messages`, { withCredentials: true });
        setContactMessages(data);
      } else if (activeTab === 'newsletter') {
        const { data } = await axios.get(`${API_URL}/api/newsletter`, { withCredentials: true });
        setNewsletterSubscribers(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load admin data'));
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    const images = formData.images
      ? formData.images.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 10)
      : [];

    if (!formData.title || !formData.content || !formData.excerpt || !formData.category) {
      toast.error('Please fill in all required fields for the article.');
      return;
    }

    try {
      const payload = {
        title: formData.title?.trim(),
        content: formData.content?.trim(),
        excerpt: formData.excerpt?.trim(),
        category: formData.category?.trim(),
        image_url: formData.image_url?.trim() || images[0] || DEFAULT_IMAGE_URL,
        images,
        featured: formData.featured || false,
        author: user?.name || 'Motoringdaily'
      };

      if (editingItem && editingItem.type === 'article') {
        await axios.put(
          `${API_URL}/api/articles/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Article updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/articles`,
          payload,
          { withCredentials: true }
        );
        toast.success('Article created successfully!');
      }

      setShowForm(false);
      setFormData({});
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, editingItem && editingItem.type === 'article' ? 'Failed to update article' : 'Failed to create article'));
    }
  };

  const handleSaveCar = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.body_type || !formData.year) {
      toast.error('Please fill in all required fields for the car.');
      return;
    }

    const videoUrl = formData.video_url ? formData.video_url.trim() : '';

    if (videoUrl && !getYouTubeEmbedUrl(videoUrl)) {
      toast.error('Invalid YouTube URL format. Use a valid YouTube video URL.');
      return;
    }

    try {
      const featuresParsed = safeJsonParse(formData.features, 'Features');
      const dimensionsParsed = safeJsonParse(formData.dimensions, 'Dimensions');
      const engineParsed = safeJsonParse(formData.engine, 'Engine');
      const parsedRating = parseFloat(formData.overall_rating);
      const images = formData.images ? formData.images.split(',').map((s) => s.trim()).filter(Boolean) : [];

      if (!Number.isFinite(parsedRating) || parsedRating < 0 || parsedRating > 5) {
        toast.error('Overall rating must be between 0 and 5');
        return;
      }

      if (!images.length) {
        toast.error('At least one image URL is required');
        return;
      }

      const payload = {
        name: formData.name,
        price: formData.price,
        images,
        body_type: formData.body_type,
        mileage: formData.mileage,
        fuel_type: formData.fuel_type,
        year: formData.year,
        transmission: formData.transmission,
        drive_type: formData.drive_type,
        video_url: videoUrl || null,
        description: formData.description || '',
        features: featuresParsed,
        dimensions: dimensionsParsed,
        engine: engineParsed,
        overall_rating: parsedRating
      };

      if (editingItem && editingItem.type === 'car') {
        await axios.put(
          `${API_URL}/api/cars/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Car updated successfully!');
      } else {
        await axios.post(
          `${API_URL}/api/cars`,
          payload,
          { withCredentials: true }
        );
        toast.success('Car created successfully!');
      }

      setShowForm(false);
      setFormData({});
      setEditingItem(null);
      fetchData();
    } catch (error) {
      if (error instanceof Error && error.message.includes('invalid JSON')) {
        toast.error(error.message);
        return;
      }
      toast.error(getApiErrorMessage(error, editingItem && editingItem.type === 'car' ? 'Failed to update car' : 'Failed to create car'));
    }
  };

  const handleEdit = (item, type) => {
    if (type === 'article') {
      setFormData({
        title: item.title,
        content: item.content,
        excerpt: item.excerpt,
        category: item.category,
        image_url: item.image_url,
        images: item.images?.join(', ') || '',
        featured: item.featured
      });
      setEditingItem({ type: 'article', id: item.id });
    } else if (type === 'car') {
      setFormData({
        name: item.name,
        price: item.price,
        images: item.images?.join(', ') || '',
        body_type: item.body_type,
        mileage: item.mileage,
        fuel_type: item.fuel_type,
        year: item.year,
        transmission: item.transmission,
        drive_type: item.drive_type,
        video_url: item.video_url || '',
        description: item.description || '',
        features: JSON.stringify(item.features),
        dimensions: JSON.stringify(item.dimensions),
        engine: JSON.stringify(item.engine),
        overall_rating: item.overall_rating
      });
      setEditingItem({ type: 'car', id: item.id });
    }
    setShowForm(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const endpoint = type === 'article' ? 'articles' : 'cars';
      await axios.delete(`${API_URL}/api/${endpoint}/${id}`, { withCredentials: true });
      toast.success(`${type} deleted successfully!`);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to delete ${type}`));
    }
  };

  const handleUpdateHomepageContent = async (section) => {
    try {
      await axios.post(
        `${API_URL}/api/homepage-content`,
        {
          section: section.section,
          title: section.title,
          description: section.description,
          cta_text: section.cta_text || '',
          cta_link: section.cta_link || '',
          video_url: section.video_url || '',
          image_url: section.image_url || ''
        },
        { withCredentials: true }
      );
      toast.success('Homepage content updated successfully!');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update homepage content'));
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to remove this subscriber?')) return;

    try {
      await axios.delete(`${API_URL}/api/newsletter/${subscriberId}`, { withCredentials: true });
      toast.success('Subscriber removed successfully!');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to remove subscriber'));
    }
  };

  const handleExportSubscribers = () => {
    const header = 'Email,Subscribed Date\n';
    const rows = filteredSubscribers
      .map((subscriber) => {
        const dateValue = subscriber.created_at || subscriber.createdAt;
        const dateText = dateValue ? new Date(dateValue).toISOString() : '';
        return `"${subscriber.email}","${dateText}"`;
      })
      .join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'newsletter-subscribers.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredSubscribers = newsletterSubscribers.filter((subscriber) =>
    subscriber.email.toLowerCase().includes(newsletterSearch.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-black mb-8 font-heading">Motoringdaily Dashboard</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-8">
          <button
            onClick={() => setActiveTab('articles')}
            className={`py-2 px-4 sm:px-6 rounded-none font-bold uppercase tracking-wider transition-colors text-sm ${
              activeTab === 'articles'
                ? 'bg-[#00A859] text-white'
                : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white'
            }`}
            data-testid="tab-articles"
          >
            Articles
          </button>
          <button
            onClick={() => setActiveTab('cars')}
            className={`py-2 px-4 sm:px-6 rounded-none font-bold uppercase tracking-wider transition-colors text-sm ${
              activeTab === 'cars'
                ? 'bg-[#00A859] text-white'
                : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white'
            }`}
            data-testid="tab-cars"
          >
            Reviews
          </button>
          <button
            onClick={() => setActiveTab('homepage')}
            className={`py-2 px-4 sm:px-6 rounded-none font-bold uppercase tracking-wider transition-colors text-sm ${
              activeTab === 'homepage'
                ? 'bg-[#00A859] text-white'
                : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white'
            }`}
            data-testid="tab-homepage"
          >
            Homepage
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`py-2 px-4 sm:px-6 rounded-none font-bold uppercase tracking-wider transition-colors text-sm ${
              activeTab === 'contact'
                ? 'bg-[#00A859] text-white'
                : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white'
            }`}
            data-testid="tab-contact"
          >
            Contact Messages
          </button>
          <button
            onClick={() => setActiveTab('newsletter')}
            className={`py-2 px-4 sm:px-6 rounded-none font-bold uppercase tracking-wider transition-colors text-sm ${
              activeTab === 'newsletter'
                ? 'bg-[#00A859] text-white'
                : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white'
            }`}
            data-testid="tab-newsletter"
          >
            Newsletter Subscribers
          </button>
        </div>

        {/* Create Button */}
        {(activeTab === 'articles' || activeTab === 'cars') && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingItem(null);
              setFormData({});
            }}
            className="mb-8 bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-6 rounded-none transition-colors duration-200"
            data-testid="create-btn"
          >
            {activeTab === 'articles' ? 'Create New Article' : 'Add Car Review'}
          </button>
        )}

        {/* Create Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 font-heading">
                {editingItem
                  ? `Edit ${editingItem.type === 'article' ? 'Article' : 'Review'}`
                  : `Create ${activeTab === 'articles' ? 'Article' : 'Review'}`}
              </h2>

              {activeTab === 'articles' ? (
                <form onSubmit={handleSaveArticle} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Title"
                    required
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    data-testid="article-title-input"
                  />
                  <input
                    type="text"
                    placeholder="Category (News, Reviews, EV, Performance, Luxury, Technology)"
                    required
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <input
                    type="text"
                    placeholder="Main Image URL"
                    required
                    value={formData.image_url || ''}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder="Additional Images URLs (comma-separated, up to 10)"
                    value={formData.images || ''}
                    onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                    rows="2"
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder="Excerpt"
                    required
                    rows="2"
                    value={formData.excerpt || ''}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder="Content"
                    required
                    rows="6"
                    value={formData.content || ''}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured || false}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="mr-2"
                    />
                    Featured Article
                  </label>
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                    >
                      {editingItem && editingItem.type === 'article' ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setFormData({});
                        setEditingItem(null);
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSaveCar} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Car Name"
                    required
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <input
                    type="text"
                    placeholder="Price (e.g., $45,000)"
                    required
                    value={formData.price || ''}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <input
                    type="text"
                    placeholder="Image URLs (comma-separated)"
                    required
                    value={formData.images || ''}
                    onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Body Type"
                      required
                      value={formData.body_type || ''}
                      onChange={(e) => setFormData({ ...formData, body_type: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="Mileage"
                      required
                      value={formData.mileage || ''}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="Fuel Type"
                      required
                      value={formData.fuel_type || ''}
                      onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="Year"
                      required
                      value={formData.year || ''}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="Transmission"
                      required
                      value={formData.transmission || ''}
                      onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="Drive Type"
                      required
                      value={formData.drive_type || ''}
                      onChange={(e) => setFormData({ ...formData, drive_type: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Overall Rating (0-5)"
                    required
                    value={formData.overall_rating || ''}
                    onChange={(e) => setFormData({ ...formData, overall_rating: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <input
                    type="text"
                    placeholder="YouTube Embed URL (optional, e.g., https://www.youtube.com/embed/VIDEO_ID)"
                    value={formData.video_url || ''}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder="Description (detailed review content)"
                    rows="6"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder='Features JSON (e.g., {"Interior": ["Leather seats"], "Safety": ["ABS"]})'
                    required
                    rows="3"
                    value={formData.features || ''}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder='Dimensions JSON (e.g., {"length": "4800mm", "width": "1900mm"})'
                    required
                    rows="2"
                    value={formData.dimensions || ''}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder='Engine JSON (e.g., {"type": "V6", "displacement": "3.0L"})'
                    required
                    rows="2"
                    value={formData.engine || ''}
                    onChange={(e) => setFormData({ ...formData, engine: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                    >
                      {editingItem && editingItem.type === 'car' ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setFormData({});
                        setEditingItem(null);
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Homepage Content Editor */}
        {activeTab === 'homepage' && (
          <div className="space-y-6 mb-8">
            {homepageContent.map((section, idx) => (
              <div key={idx} className="bg-white p-6 border border-[#E5E7EB]">
                <h3 className="text-xl font-bold mb-4 font-heading capitalize">
                  {section.section.replace('_', ' ')}
                </h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Title"
                    value={section.title}
                    onChange={(e) => {
                      const updated = [...homepageContent];
                      updated[idx].title = e.target.value;
                      setHomepageContent(updated);
                    }}
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <textarea
                    placeholder="Description"
                    value={section.description}
                    onChange={(e) => {
                      const updated = [...homepageContent];
                      updated[idx].description = e.target.value;
                      setHomepageContent(updated);
                    }}
                    rows="3"
                    className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="CTA Text"
                      value={section.cta_text || ''}
                      onChange={(e) => {
                        const updated = [...homepageContent];
                        updated[idx].cta_text = e.target.value;
                        setHomepageContent(updated);
                      }}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                    <input
                      type="text"
                      placeholder="CTA Link (e.g., /article/article-id or leave empty for featured article)"
                      value={section.cta_link || ''}
                      onChange={(e) => {
                        const updated = [...homepageContent];
                        updated[idx].cta_link = e.target.value;
                        setHomepageContent(updated);
                      }}
                      className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                    />
                  </div>
                  {section.section === 'video_showcase' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Video URL (YouTube embed)"
                        value={section.video_url || ''}
                        onChange={(e) => {
                          const updated = [...homepageContent];
                          updated[idx].video_url = e.target.value;
                          setHomepageContent(updated);
                        }}
                        className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                      />
                      <input
                        type="text"
                        placeholder="Image URL"
                        value={section.image_url || ''}
                        onChange={(e) => {
                          const updated = [...homepageContent];
                          updated[idx].image_url = e.target.value;
                          setHomepageContent(updated);
                        }}
                        className="w-full px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                      />
                    </div>
                  )}
                  <button
                    onClick={() => handleUpdateHomepageContent(section)}
                    className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                  >
                    Update
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Data Table */}
        {activeTab !== 'homepage' && activeTab !== 'contact' && activeTab !== 'newsletter' && (
          <div className="bg-white p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#E5E7EB]">
                {activeTab === 'articles' ? (
                  <>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Title</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Category</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Author</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Featured</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Actions</th>
                  </>
                ) : (
                  <>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Name</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Price</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Year</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Rating</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {activeTab === 'articles'
              ? (articles.length ? articles.map((article) => (
                    <tr key={article.id} className="border-b border-[#E5E7EB]">
                      <td className="py-3 px-4">{article.title}</td>
                      <td className="py-3 px-4">{article.category}</td>
                      <td className="py-3 px-4">{article.author}</td>
                      <td className="py-3 px-4">{article.featured ? 'Yes' : 'No'}</td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleEdit(article, 'article')}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid="edit-article-btn"
                            title="Edit article"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(article.id, 'article')}
                            className="text-red-600 hover:text-red-800"
                            data-testid="delete-article-btn"
                            title="Delete article"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="py-6 px-4 text-center text-[#666666]">No articles found</td>
                    </tr>
                  ))
                : (cars.length ? cars.map((car) => (
                    <tr key={car.id} className="border-b border-[#E5E7EB]">
                      <td className="py-3 px-4">{car.name}</td>
                      <td className="py-3 px-4">{car.price}</td>
                      <td className="py-3 px-4">{car.year}</td>
                      <td className="py-3 px-4">{car.overall_rating}/5</td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleEdit(car, 'car')}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid="edit-car-btn"
                            title="Edit car"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(car.id, 'car')}
                            className="text-red-600 hover:text-red-800"
                            data-testid="delete-car-btn"
                            title="Delete car"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="py-6 px-4 text-center text-[#666666]">No reviews found</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Contact Messages Table */}
        {activeTab === 'contact' && (
          <div className="bg-white p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#E5E7EB]">
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Name</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Email</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Mobile Number</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Subject</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Message</th>
                <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Date/Time</th>
              </tr>
            </thead>
            <tbody>
              {contactMessages.map((message) => (
                <tr key={message.id} className="border-b border-[#E5E7EB]">
                  <td className="py-3 px-4">{message.name}</td>
                  <td className="py-3 px-4">{message.email}</td>
                  <td className="py-3 px-4">{message.mobile || '-'}</td>
                  <td className="py-3 px-4">{message.subject}</td>
                  <td className="py-3 px-4 max-w-xs truncate" title={message.message}>{message.message}</td>
                  <td className="py-3 px-4">{new Date(message.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Newsletter Subscribers Table */}
        {activeTab === 'newsletter' && (
          <div className="space-y-4">
            <div className="bg-white p-4 border border-[#E5E7EB] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <input
                type="text"
                placeholder="Search by email"
                value={newsletterSearch}
                onChange={(e) => setNewsletterSearch(e.target.value)}
                className="w-full sm:w-96 px-4 py-2 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                data-testid="newsletter-search-input"
              />
              <button
                onClick={handleExportSubscribers}
                className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
                data-testid="newsletter-export-btn"
              >
                Export CSV
              </button>
            </div>

            <div className="bg-white p-6 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Email</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Subscribed Date</th>
                    <th className="text-left py-3 px-4 font-bold uppercase tracking-wide text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribers.map((subscriber) => {
                    const dateValue = subscriber.created_at || subscriber.createdAt;
                    return (
                      <tr key={subscriber.id || subscriber.email} className="border-b border-[#E5E7EB]">
                        <td className="py-3 px-4">{subscriber.email}</td>
                        <td className="py-3 px-4">{dateValue ? new Date(dateValue).toLocaleString() : '-'}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleDeleteSubscriber(subscriber.id || subscriber.email)}
                            className="text-red-600 hover:text-red-800"
                            data-testid="delete-subscriber-btn"
                            title="Delete subscriber"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};